import { Button, Modal, Spinner } from 'react-bootstrap'
import { useMutation } from '@apollo/client'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { DELETE_RESOURCE_CATEGORY } from '../../../gql/mutations/resourceCategoryMutations'
import { IDeleteResourceCategoryResponse, IResourceCategory } from '../../../types/resourceCategory'
import { parseId } from '../../../helpers'
import { FormattedMessage, useIntl } from 'react-intl'
import { BACKEND_ERROR, RESOURCE_MESSAGES as R_ } from '../resourceCenterMessages'

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  category: IResourceCategory
}

export const ModalDeleteCategory: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  category,
}) => {
  const intl = useIntl()
  const [deleteCategory, { data, loading, error }] =
    useMutation<IDeleteResourceCategoryResponse>(DELETE_RESOURCE_CATEGORY)

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(BACKEND_ERROR, {message: error.message}))
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.deleteResourceCategory.success) {
      onClose()
      refetch()
      toast.success(data.deleteResourceCategory.message || intl.formatMessage(R_.toastFolderDeleted))
    } else if (
      data.deleteResourceCategory.errors &&
      data.deleteResourceCategory.errors.length > 0
    ) {
      toast.error(intl.formatMessage(BACKEND_ERROR, {message: data.deleteResourceCategory.errors[0].message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDelete = async () => {
    try {
      await deleteCategory({ variables: { id: parseId(category.id) } })
    } catch (err) {
      console.error('Error deleting category:', err)
      toast.error(intl.formatMessage(R_.errDeleteFolder))
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton className="bg-danger text-white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-triangle-fill" /> {intl.formatMessage(R_.deleteFolder)}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-3">
          <FormattedMessage
            {...R_.confirmDeleteFolder}
            values={{name: category.name, b: (c) => <strong>{c}</strong>}}
          />
        </p>

        {category.childrenCount > 0 && (
          <div className="alert alert-warning mb-3">
            <i className="bi bi-exclamation-triangle me-2" />
            <FormattedMessage
              {...R_.folderHasSubfolders}
              values={{count: category.childrenCount, b: (c) => <strong>{c}</strong>}}
            />
          </div>
        )}

        <div className="p-3 border rounded bg-light small">
          <i className="bi bi-info-circle me-2 text-primary" />
          <FormattedMessage
            {...R_.folderResourcesKept}
            values={{b: (c) => <strong>{c}</strong>}}
          />
          {category.resourcesCount > 0 && (
            <div className="mt-1">
              <FormattedMessage
                {...R_.folderResourceCount}
                values={{count: category.resourcesCount, b: (c) => <strong>{c}</strong>}}
              />
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {intl.formatMessage(R_.cancel)}
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={loading || category.childrenCount > 0}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(R_.deleting)}
            </>
          ) : (
            <>
              <i className="bi bi-trash me-1" /> {intl.formatMessage(R_.yesDeleteFolder)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
